import api from '../utils/axiosConfig';
import {
  IncidentMemory,
  CuratorNotification,
  RecallGap,
  LowRecallTicket,
  MissedChunk,
  PipelineTrace,
} from '../types';

export class CuratorService {
  // ── Memory batch-fetch ─────────────────────────────────────────────────────

  static async getMemories(ids: string[], clientId: string): Promise<IncidentMemory[]> {
    try {
      const response = await api.get('/curator/memories', {
        params: { ids: ids.join(','), client_id: clientId },
      });
      return response.data.memories ?? [];
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch past incidents');
    }
  }

  // ── Notifications ──────────────────────────────────────────────────────────

  static async getNotifications(clientId: string): Promise<{ notifications: CuratorNotification[] }> {
    try {
      const response = await api.get('/curator/notifications', { params: { client_id: clientId } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch notifications');
    }
  }

  static async dismissNotification(id: string): Promise<void> {
    try {
      await api.post(`/curator/notifications/${id}/dismiss`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to dismiss notification');
    }
  }

  static async scanSystemic(clientId: string): Promise<void> {
    try {
      await api.post('/curator/notifications/scan-systemic', { client_id: clientId });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to start systemic scan');
    }
  }

  // ── Coverage analysis ──────────────────────────────────────────────────────

  static async getRecallGaps(clientId: string): Promise<{ gaps: RecallGap[] }> {
    try {
      const response = await api.get('/curator/recall-gaps', { params: { client_id: clientId } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch recall gaps');
    }
  }

  static async getLowRecallTickets(clientId: string): Promise<{ tickets: LowRecallTicket[] }> {
    try {
      const response = await api.get('/curator/low-recall-tickets', { params: { client_id: clientId } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch low recall tickets');
    }
  }

  static async getMissedChunks(clientId: string): Promise<{ chunks: MissedChunk[] }> {
    try {
      const response = await api.get('/curator/missed-chunks', { params: { client_id: clientId } });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch missed chunks');
    }
  }

  static async flagSystemic(memoryId: string): Promise<void> {
    try {
      await api.post(`/curator/missed-chunks/${memoryId}/flag-systemic`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to flag systemic miss');
    }
  }

  // ── Pipeline traces ────────────────────────────────────────────────────────

  static async getTicketTrace(ticketId: string): Promise<PipelineTrace> {
    try {
      const response = await api.get(`/curator/tickets/${ticketId}/trace`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch ticket trace');
    }
  }

  static async getRunTrace(entryId: string, runId: string): Promise<PipelineTrace> {
    try {
      const response = await api.get(`/curator/golden-set/${entryId}/runs/${runId}/trace`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch run trace');
    }
  }

  // ── Golden set curation ────────────────────────────────────────────────────

  static async addHint(entryId: string, body: { hint_text?: string; memory_id: string }): Promise<{ new_summary: string | null; new_next_steps: { step: string; rationale: string }[] }> {
    try {
      const response = await api.post(`/curator/golden-set/${entryId}/hints`, body);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to add hint');
    }
  }

  static async updateCStar(entryId: string, body: { add: string[]; remove: string[] }): Promise<void> {
    try {
      await api.put(`/curator/golden-set/${entryId}/c-star`, body);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update C*');
    }
  }

  static async updateBaseline(entryId: string, body: { baseline_summary: string; baseline_next_steps: { step: string; rationale: string }[] }): Promise<void> {
    try {
      await api.put(`/curator/golden-set/${entryId}/baseline`, body);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to update baseline');
    }
  }
}
